import {
  Badge,
  CircularProgress,
  IconButton,
  Menu,
  Tooltip,
} from "@mui/material";
import { keyframes, styled } from "@mui/material/styles";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { IUserNotification } from "../../Interface/Shared/Notification";
import RequisitionType from "../../Services/Requisition/RequisitionTypeService";
import {
  downloadBlobDirectly,
  openSaveFileDialog,
  writeBlobToHandle,
} from "../../Utils/Common/SaveFileDialog";
import useUserNotifications from "../hooks/useUserNotifications";
import useLang from "./../hooks/useLanguage";

const PRIMARY = "#69A54B";
const PRIMARY_SOFT = "rgba(105, 165, 75, 0.10)";
const MUTED = "#7e8299";

const isExternalLink = (link: string) => /^https?:\/\//i.test(link);

/** Last path segment of the link, used as the download file name */
const fileNameFromLink = (link: string) => {
  try {
    const path = new URL(link, window.location.origin).pathname;
    return decodeURIComponent(path.split("/").pop() || "") || "report";
  } catch {
    return "report";
  }
};

/* -------------------------------------------------------------------------- */
/* Animations                                                                  */
/* -------------------------------------------------------------------------- */

/** Bell wobble, played when a new notification arrives over SignalR */
const ring = keyframes`
  0%, 70%, 100% { transform: rotate(0deg); }
  10% { transform: rotate(14deg); }
  20% { transform: rotate(-11deg); }
  30% { transform: rotate(8deg); }
  40% { transform: rotate(-6deg); }
  50% { transform: rotate(4deg); }
  60% { transform: rotate(-2deg); }
`;

/** Halo around the unread dot */
const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(105, 165, 75, 0.55); }
  70% { box-shadow: 0 0 0 5px rgba(105, 165, 75, 0); }
  100% { box-shadow: 0 0 0 0 rgba(105, 165, 75, 0); }
`;

/** Rows ease in as the menu opens */
const riseIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: none; }
`;

/* -------------------------------------------------------------------------- */
/* Styled pieces                                                               */
/* -------------------------------------------------------------------------- */

const BellSvg = styled("svg", {
  shouldForwardProp: (prop) => prop !== "animate",
})<{ animate: boolean }>(({ animate }) => ({
  width: 19,
  height: 19,
  display: "block",
  transformOrigin: "50% 15%",
  animation: animate ? `${ring} 0.9s ease-in-out` : "none",
}));

/** Outline download: arrow into an open tray */
const DownloadIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    style={{ display: "block", flexShrink: 0 }}
  >
    <path d="M12 3.5v11" />
    <path d="m7.5 10 4.5 4.5 4.5-4.5" />
    <path d="M4.5 15.5v1.2a3.3 3.3 0 0 0 3.3 3.3h8.4a3.3 3.3 0 0 0 3.3-3.3v-1.2" />
  </svg>
);

/** Outline bell: rounded stroke, knob on top, clapper under the rim */
const BellIcon = ({ animate }: { animate: boolean }) => (
  <BellSvg
    animate={animate}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="12" cy="3.3" r="1.15" />
    <path d="M6 10.6a6 6 0 0 1 12 0v4.1c0 .42.16.82.44 1.13l1.05 1.15c.6.66.14 1.72-.75 1.72H5.26c-.89 0-1.35-1.06-.75-1.72l1.05-1.15c.28-.31.44-.71.44-1.13v-4.1Z" />
    <path d="M9.85 19.4a2.15 2.15 0 0 0 4.3 0" />
  </BellSvg>
);

const Panel = styled("div")({
  display: "flex",
  flexDirection: "column",
  maxHeight: 400,
});

const PanelHeader = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
  padding: "11px 14px",
  flexShrink: 0,
});

const HeaderTitle = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 7,
  fontSize: 14,
  fontWeight: 600,
  color: "#181c32",
  letterSpacing: "-0.01em",
});

const CountPill = styled("span")({
  backgroundColor: PRIMARY_SOFT,
  color: PRIMARY,
  borderRadius: "50%",
  width: 19,
  height: 19,
  fontSize: 11,
  fontWeight: 600,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
});

const TextAction = styled("button")({
  border: "none",
  background: "none",
  padding: 0,
  fontSize: 12,
  fontWeight: 500,
  color: PRIMARY,
  cursor: "pointer",
  whiteSpace: "nowrap",
  transition: "opacity 0.15s ease",
  "&:hover": { opacity: 0.7 },
});

const ScrollArea = styled("div")({
  overflowY: "auto",
  overscrollBehavior: "contain",
  borderTop: "1px solid #f1f3f5",
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#dfe2e6",
    borderRadius: 3,
  },
});

const Row = styled("div", {
  shouldForwardProp: (prop) => prop !== "unread",
})<{ unread: boolean }>(({ unread }) => ({
  display: "flex",
  gap: 10,
  padding: "10px 14px",
  borderBottom: "1px solid #f1f3f5",
  backgroundColor: unread ? "rgba(105, 165, 75, 0.035)" : "#fff",
  animation: `${riseIn} 0.22s ease both`,
  transition: "background-color 0.25s ease",
  "&:hover": {
    backgroundColor: unread ? "rgba(105, 165, 75, 0.07)" : "#fafbfc",
  },
  "&:last-of-type": { borderBottom: "none" },
}));

const RowIcon = styled("span")({
  flexShrink: 0,
  width: 30,
  height: 30,
  borderRadius: 8,
  backgroundColor: "#f1f4f2",
  color: PRIMARY,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 13,
});

const RowTitle = styled("div")({
  fontSize: 13,
  fontWeight: 600,
  color: "#181c32",
  lineHeight: 1.3,
  wordBreak: "break-word",
});

const RowMessage = styled("div")({
  fontSize: 12,
  color: MUTED,
  lineHeight: 1.35,
  marginTop: 1,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
});

const RowTime = styled("div")({
  fontSize: 11.5,
  color: "#a1a5b7",
  whiteSpace: "nowrap",
  flexShrink: 0,
});

/** Filled green while unread, flat grey once seen */
const StatusDot = styled("span", {
  shouldForwardProp: (prop) => prop !== "unread",
})<{ unread: boolean }>(({ unread }) => ({
  width: 7,
  height: 7,
  borderRadius: "50%",
  flexShrink: 0,
  backgroundColor: unread ? PRIMARY : "#e4e6ef",
  transition: "background-color 0.3s ease",
  animation: unread ? `${pulse} 2.4s ease-out infinite` : "none",
}));

const OutlineButton = styled("button", {
  shouldForwardProp: (prop) => prop !== "tone",
})<{ tone: "primary" | "muted" }>(({ tone }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  height: 26,
  padding: "0 10px",
  borderRadius: 6,
  fontSize: 11.5,
  fontWeight: 600,
  lineHeight: 1,
  cursor: "pointer",
  backgroundColor: "transparent",
  border: `1px solid ${tone === "primary" ? "#d4e4cb" : "#e4e6ef"}`,
  color: tone === "primary" ? PRIMARY : MUTED,
  transition:
    "background-color 0.15s ease, border-color 0.15s ease, transform 0.12s ease",
  "&:hover": {
    backgroundColor: tone === "primary" ? PRIMARY_SOFT : "#f4f6f8",
    borderColor: tone === "primary" ? PRIMARY : "#d6d9e0",
  },
  "&:active": { transform: "scale(0.97)" },
  "&:disabled": { opacity: 0.6, cursor: "default", transform: "none" },
}));

const LoadingFooter = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 7,
  padding: "11px 14px",
  fontSize: 11.5,
  color: MUTED,
  borderTop: "1px solid #f1f3f5",
});

const EmptyState = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 7,
  padding: "32px 16px",
  color: MUTED,
  fontSize: 12,
  animation: `${riseIn} 0.25s ease both`,
});

/* -------------------------------------------------------------------------- */

interface NotificationBellProps {
  /** "top" opens the panel upwards (sidebar footer), "bottom" downwards (header) */
  placement?: "top" | "bottom";
}

/**
 * Bell in the header: shows the unread notification count, and on click loads
 * and lists the logged in user's notifications. New notifications arrive in
 * real time over SignalR (`userNotificationReceived`).
 */
const NotificationBell = ({ placement = "top" }: NotificationBellProps) => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [isRinging, setIsRinging] = useState(false);
  const open = Boolean(anchorEl);

  const {
    notifications,
    unseenCount,
    hasMore,
    isLoadingMore,
    fetchNotifications,
    loadMore,
    markAsSeen,
    markAllAsSeen,
  } = useUserNotifications();

  /** Pull the next page once the list is scrolled near its end */
  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!hasMore || isLoadingMore) {
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 48) {
      loadMore();
    }
  };

  // Ring the bell only when the count grows, not when it drops on mark-as-seen
  const previousCountRef = useRef(unseenCount);
  useEffect(() => {
    if (unseenCount > previousCountRef.current) {
      setIsRinging(true);
      const timer = setTimeout(() => setIsRinging(false), 900);
      previousCountRef.current = unseenCount;
      return () => clearTimeout(timer);
    }
    previousCountRef.current = unseenCount;
  }, [unseenCount]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    // Refresh on open so the list is accurate even if an event was missed
    fetchNotifications();
  };

  const handleClose = () => setAnchorEl(null);

  const handleView = (notification: IUserNotification) => {
    const link = notification?.link;
    if (!link) {
      return;
    }

    if (isExternalLink(link)) {
      window.open(link, "_blank", "noopener,noreferrer");
    } else {
      navigate(link);
    }
    handleClose();
  };

  /**
   * Asks where to save through the OS Save As dialog, then writes the report
   * there. The stored link is not readable directly, so File/ShowBlob resolves
   * it into a fetchable URL first, the same way the Download Reports tab does.
   * Browsers without the file picker fall back to the download folder.
   */
  const handleDownload = async (notification: IUserNotification) => {
    const link = notification?.link;
    if (!link) {
      return;
    }

    // The Save As dialog must open while the click's user activation is still
    // valid, before any API round-trip.
    const fileName = fileNameFromLink(link);
    const fileHandle = await openSaveFileDialog(fileName);
    if (fileHandle === "cancelled") {
      return;
    }

    setDownloadingId(notification?.id ?? null);
    let resolvedUrl = "";
    try {
      const response: any = await RequisitionType.ShowBlob(link);
      resolvedUrl = response?.data?.Data?.replace("}", "") ?? "";

      if (resolvedUrl) {
        const file = await fetch(resolvedUrl);
        if (!file.ok) {
          throw new Error(`Failed to fetch report file: ${file.status}`);
        }
        const blob = await file.blob();
        if (fileHandle) {
          await writeBlobToHandle(fileHandle, blob);
        } else {
          downloadBlobDirectly(blob, fileName);
        }
        toast.success(t("Report downloaded"));
        return;
      }
    } catch (error) {
      // Blob storage may block cross-origin reads; fall back to opening the URL
      console.error("Falling back to opening the report in a new tab:", error);
    } finally {
      setDownloadingId(null);
    }

    window.open(resolvedUrl || link, "_blank", "noopener,noreferrer");
  };

  const handleMarkSeen = (notification: IUserNotification) => {
    if (typeof notification?.id === "number") {
      markAsSeen([notification.id]);
    }
  };

  const handleMarkAllAsSeen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    markAllAsSeen();
  };

  const opensUpwards = placement === "top";

  return (
    <>
      <Tooltip title={t("Notifications")} arrow placement={placement}>
        <IconButton
          aria-controls={open ? "notification-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          aria-label={t("Notifications")}
          onClick={handleOpen}
          data-test-id="notification-bell-button"
          sx={{
            width: 34,
            height: 34,
            flexShrink: 0,
            color: unseenCount > 0 ? PRIMARY : MUTED,
            transition: "background-color 0.15s ease, color 0.15s ease",
            "&:hover": {
              backgroundColor: PRIMARY_SOFT,
              color: PRIMARY,
            },
          }}
        >
          <Badge
            badgeContent={unseenCount}
            max={99}
            color="error"
            overlap="circular"
            sx={{
              "& .MuiBadge-badge": {
                fontSize: 9,
                fontWeight: 700,
                height: 15,
                minWidth: 15,
                padding: "0 4px",
                transition: "transform 0.2s ease",
              },
            }}
          >
            <BellIcon animate={isRinging} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        id="notification-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "notification-bell-button",
          sx: { padding: 0 },
        }}
        anchorOrigin={{
          vertical: opensUpwards ? "top" : "bottom",
          horizontal: opensUpwards ? "right" : "right",
        }}
        transformOrigin={{
          vertical: opensUpwards ? "bottom" : "top",
          horizontal: opensUpwards ? "left" : "right",
        }}
        PaperProps={{
          sx: {
            width: 340,
            maxWidth: "calc(100vw - 24px)",
            borderRadius: 2.5,
            marginTop: opensUpwards ? 0 : "6px",
            marginBottom: opensUpwards ? "6px" : 0,
            boxShadow: "0 8px 28px rgba(24, 28, 50, 0.14)",
            overflow: "hidden",
          },
        }}
      >
        <Panel>
          <PanelHeader>
            <HeaderTitle>
              {t("Notifications")}
              {unseenCount > 0 ? (
                <CountPill>{unseenCount > 99 ? "99+" : unseenCount}</CountPill>
              ) : null}
            </HeaderTitle>
            {unseenCount > 0 ? (
              <TextAction
                type="button"
                onClick={handleMarkAllAsSeen}
                data-test-id="notification-mark-all-seen"
              >
                {t("Mark all as read")}
              </TextAction>
            ) : null}
          </PanelHeader>

          <ScrollArea onScroll={handleScroll}>
            {notifications.length === 0 ? (
              <EmptyState data-test-id="notification-empty">
                <i className="bi bi-bell-slash" style={{ fontSize: 21 }} />
                <span>{t("No notifications")}</span>
              </EmptyState>
            ) : (
              notifications.map((notification: IUserNotification, index) => {
                const unread = !notification?.seenAt;
                const isDownloading = downloadingId === notification?.id;
                const hasLink = Boolean(notification?.link);
                // The server sends the relative time; a live SignalR payload
                // without it falls back to working it out from createdAt
                const timeAgo =
                  notification?.notificationAgo?.trim() ||
                  (notification?.createdAt
                    ? moment(notification.createdAt).fromNow(true)
                    : "");

                return (
                  <Row
                    key={notification?.id}
                    unread={unread}
                    // Light stagger on the rows that are visible at open time
                    style={{ animationDelay: `${Math.min(index, 7) * 28}ms` }}
                    data-test-id={`notification-item-${notification?.id}`}
                  >
                    <RowIcon>
                      <i
                        className={`bi ${
                          notification?.isDownloadable
                            ? "bi-file-earmark-arrow-down"
                            : notification?.isRedirectable
                              ? "bi-box-arrow-up-right"
                              : "bi-bell"
                        }`}
                      />
                    </RowIcon>

                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 8,
                        }}
                      >
                        <RowTitle style={{ flex: 1 }}>
                          {notification?.messageTitle}
                        </RowTitle>
                        {timeAgo ? <RowTime>{timeAgo}</RowTime> : null}
                        <StatusDot unread={unread} style={{ marginTop: 4 }} />
                      </div>

                      <RowMessage>{notification?.message}</RowMessage>

                      {(notification?.isRedirectable && hasLink) ||
                      (notification?.isDownloadable && hasLink) ||
                      unread ? (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 6,
                            marginTop: 7,
                          }}
                        >
                          {notification?.isRedirectable && hasLink ? (
                            <OutlineButton
                              type="button"
                              tone="primary"
                              onClick={() => handleView(notification)}
                              data-test-id={`notification-view-${notification?.id}`}
                            >
                              <i className="bi bi-box-arrow-up-right" />
                              {t("View")}
                            </OutlineButton>
                          ) : null}

                          {notification?.isDownloadable && hasLink ? (
                            <OutlineButton
                              type="button"
                              tone="primary"
                              onClick={() => handleDownload(notification)}
                              disabled={isDownloading}
                              data-test-id={`notification-download-${notification?.id}`}
                            >
                              {isDownloading ? (
                                <CircularProgress
                                  size={11}
                                  thickness={5}
                                  sx={{ color: PRIMARY }}
                                />
                              ) : (
                                <DownloadIcon />
                              )}
                              {isDownloading
                                ? t("Downloading...")
                                : t("Download")}
                            </OutlineButton>
                          ) : null}

                          {unread ? (
                            <OutlineButton
                              type="button"
                              tone="muted"
                              onClick={() => handleMarkSeen(notification)}
                              data-test-id={`notification-viewed-${notification?.id}`}
                            >
                              <i className="bi bi-check2" />
                              {t("Mark as read")}
                            </OutlineButton>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </Row>
                );
              })
            )}

            {isLoadingMore ? (
              <LoadingFooter data-test-id="notification-loading-more">
                <CircularProgress
                  size={13}
                  thickness={5}
                  sx={{ color: PRIMARY }}
                />
                {t("Loading...")}
              </LoadingFooter>
            ) : null}
          </ScrollArea>
        </Panel>
      </Menu>
    </>
  );
};

export default NotificationBell;
